import {useCallback} from 'react';

import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

export enum StorableRelevantItemsFilterOption {
  ALL = 'All',
  ENTRIES = 'Entries',
  ENTITIES = 'Entities',
  INVENTORY = 'Inventory',
  PROCESSES = 'Task groups',
  WORKSHEET_STEPS = 'Worksheet steps',
}

export interface StorableRelevantItemsFilteredTableState {
  searchQuery: string;
  filterOption: StorableRelevantItemsFilterOption;
  pageOffset: number;
}

export const storableRelevantItemsFilteredTableState =
  makeUseGlobalClientState<StorableRelevantItemsFilteredTableState>({
    searchQuery: '',
    filterOption: StorableRelevantItemsFilterOption.ALL,
    pageOffset: 0,
  });

export type UpdateStorableRelevantItemsFilteredTableState = (
  patch: Partial<StorableRelevantItemsFilteredTableState>
) => void;

function patchResetsPageOffset(patch: Partial<StorableRelevantItemsFilteredTableState>): boolean {
  return Object.hasOwn(patch, 'filterOption') || Object.hasOwn(patch, 'searchQuery');
}

export function mergeStorableRelevantItemsFilteredTableStatePatch(
  state: StorableRelevantItemsFilteredTableState,
  patch: Partial<StorableRelevantItemsFilteredTableState>
): StorableRelevantItemsFilteredTableState {
  const nextState = {...state, ...patch};
  if (patchResetsPageOffset(patch)) {
    nextState.pageOffset = 0;
  }
  return nextState;
}

export function useStorableRelevantItemsFilteredTableState(
  storableId: string
): [StorableRelevantItemsFilteredTableState, UpdateStorableRelevantItemsFilteredTableState] {
  const [state, setState] = storableRelevantItemsFilteredTableState.useGlobalClientState(storableId);

  const updateFilteredTableState = useCallback(
    (patch: Partial<StorableRelevantItemsFilteredTableState>) => {
      setState(mergeStorableRelevantItemsFilteredTableStatePatch(state, patch));
    },
    [state, setState]
  );

  return [state, updateFilteredTableState];
}
